/**
 * Video Renderer
 *
 * Renders video cinemagraphs with seamless transitions from CSS animations.
 * Handles video playback, loading states, and fallback to CSS on failure.
 */

/**
 * Video playback state
 */
export type VideoPlaybackState = 'loading' | 'buffering' | 'playing' | 'paused' | 'error';

/**
 * Video renderer configuration
 */
export interface VideoRendererConfig {
    /** Transition duration for CSS to video swap (ms) */
    transitionDuration?: number;
    /** Enable debug logging */
    debug?: boolean;
    /** Auto-play when video is ready */
    autoPlay?: boolean;
    /** Loop video playback */
    loop?: boolean;
    /** Mute video (required for autoplay) */
    muted?: boolean;
}

/**
 * Default configuration
 */
const DEFAULT_CONFIG: Required<VideoRendererConfig> = {
    transitionDuration: 500,
    debug: false,
    autoPlay: true,
    loop: true,
    muted: true,
};

/**
 * Active video entry
 */
interface ActiveVideo {
    /** The element being animated (img or element with background-image) */
    targetElement: HTMLElement;
    videoElement: HTMLVideoElement;
    wrapperElement: HTMLDivElement;
    state: VideoPlaybackState;
    onFallback?: () => void;
}

/**
 * VideoRenderer class
 *
 * Handles seamless video overlay and playback with CSS transition effects.
 */
export class VideoRenderer {
    private config: Required<VideoRendererConfig>;
    private activeVideos: Map<string, ActiveVideo> = new Map();

    constructor(config: VideoRendererConfig = {}) {
        this.config = {
            ...DEFAULT_CONFIG,
            ...config,
        };
    }

    /**
     * Prepare a video overlay for an element
     *
     * @param imageId - Unique ID for tracking
     * @param targetElement - The element to overlay (img or element with background-image)
     * @param videoUrl - URL of the video to play
     * @param onFallback - Callback if video fails to load
     * @returns Promise that resolves when video is ready to play
     */
    async prepareVideo(
        imageId: string,
        targetElement: HTMLElement,
        videoUrl: string,
        onFallback?: () => void
    ): Promise<boolean> {
        // Check if already prepared
        if (this.activeVideos.has(imageId)) {
            this.log(`⚠️ Video already prepared: ${imageId}`);
            return true;
        }

        try {
            // Create wrapper to contain element and video
            const wrapper = this.createWrapper(targetElement);

            // Create video element
            const video = this.createVideoElement(videoUrl);

            // Add video to wrapper (behind element initially)
            wrapper.insertBefore(video, targetElement);

            // Track the video
            const entry: ActiveVideo = {
                targetElement,
                videoElement: video,
                wrapperElement: wrapper,
                state: 'loading',
                onFallback,
            };
            this.activeVideos.set(imageId, entry);

            // Wait for video to be ready
            await this.waitForVideoReady(video, entry);

            this.log(`✅ Video prepared: ${imageId}`);
            return true;

        } catch (error) {
            this.log(`❌ Prepare failed: ${imageId} - ${error}`);
            this.cleanup(imageId);
            onFallback?.();
            return false;
        }
    }

    /**
     * Play video and transition from CSS animation
     */
    play(imageId: string): boolean {
        const entry = this.activeVideos.get(imageId);
        if (!entry) {
            this.log(`⚠️ No video found: ${imageId}`);
            return false;
        }

        if (entry.state === 'error') {
            this.log(`⚠️ Video in error state: ${imageId}`);
            return false;
        }

        try {
            // Fade out image, fade in video
            this.transitionToVideo(entry);

            // Start video playback
            entry.videoElement.play().catch(err => {
                this.log(`❌ Play failed: ${err}`);
                entry.state = 'error';
                entry.onFallback?.();
            });

            entry.state = 'playing';
            this.log(`▶️ Playing: ${imageId}`);
            return true;

        } catch (error) {
            this.log(`❌ Play error: ${error}`);
            entry.state = 'error';
            entry.onFallback?.();
            return false;
        }
    }

    /**
     * Pause video playback
     */
    pause(imageId: string): boolean {
        const entry = this.activeVideos.get(imageId);
        if (!entry || entry.state !== 'playing') {
            return false;
        }

        entry.videoElement.pause();
        entry.state = 'paused';
        this.log(`⏸️ Paused: ${imageId}`);
        return true;
    }

    /**
     * Resume video playback
     */
    resume(imageId: string): boolean {
        const entry = this.activeVideos.get(imageId);
        if (!entry || entry.state !== 'paused') {
            return false;
        }

        entry.videoElement.play().catch(() => {
            entry.state = 'error';
        });
        entry.state = 'playing';
        this.log(`▶️ Resumed: ${imageId}`);
        return true;
    }

    /**
     * Stop video and remove overlay
     */
    stop(imageId: string): void {
        const entry = this.activeVideos.get(imageId);
        if (!entry) return;

        // Transition back to image
        this.transitionToImage(entry);

        // Cleanup after transition
        setTimeout(() => {
            this.cleanup(imageId);
        }, this.config.transitionDuration);
    }

    /**
     * Check if video is playing for an image
     */
    isPlaying(imageId: string): boolean {
        const entry = this.activeVideos.get(imageId);
        return entry?.state === 'playing';
    }

    /**
     * Check if video is prepared for an image
     */
    isPrepared(imageId: string): boolean {
        return this.activeVideos.has(imageId);
    }

    /**
     * Get current playback state
     */
    getState(imageId: string): VideoPlaybackState | null {
        return this.activeVideos.get(imageId)?.state || null;
    }

    /**
     * Destroy all videos and cleanup
     */
    destroy(): void {
        for (const imageId of this.activeVideos.keys()) {
            this.cleanup(imageId);
        }
        this.log(`🧹 Destroyed all videos`);
    }

    /**
     * Create wrapper element around target element
     */
    private createWrapper(targetElement: HTMLElement): HTMLDivElement {
        // Check if already wrapped
        const existingWrapper = targetElement.parentElement;
        if (existingWrapper?.classList.contains('beautifi-video-wrapper')) {
            return existingWrapper as HTMLDivElement;
        }

        const wrapper = document.createElement('div');
        wrapper.className = 'beautifi-video-wrapper';
        wrapper.style.cssText = `
            position: relative;
            display: inline-block;
            width: ${targetElement.offsetWidth}px;
            height: ${targetElement.offsetHeight}px;
            overflow: hidden;
        `;

        // Wrap the element
        targetElement.parentNode?.insertBefore(wrapper, targetElement);
        wrapper.appendChild(targetElement);

        // Style the element for overlay
        targetElement.style.cssText += `
            position: relative;
            z-index: 2;
            transition: opacity ${this.config.transitionDuration}ms ease-in-out;
        `;

        return wrapper;
    }

    /**
     * Create video element with proper attributes
     */
    private createVideoElement(videoUrl: string): HTMLVideoElement {
        const video = document.createElement('video');
        video.src = videoUrl;
        video.muted = this.config.muted;
        video.loop = this.config.loop;
        video.playsInline = true;
        video.preload = 'auto';
        video.crossOrigin = 'anonymous';

        video.style.cssText = `
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            object-fit: cover;
            z-index: 1;
            opacity: 0;
            transition: opacity ${this.config.transitionDuration}ms ease-in-out;
        `;

        return video;
    }

    /**
     * Wait for video to be ready to play
     */
    private waitForVideoReady(video: HTMLVideoElement, entry: ActiveVideo): Promise<void> {
        return new Promise((resolve, reject) => {
            const timeout = setTimeout(() => {
                reject(new Error('Video load timeout'));
            }, 30000); // 30s timeout

            video.addEventListener('canplaythrough', () => {
                clearTimeout(timeout);
                entry.state = 'buffering';
                resolve();
            }, { once: true });

            video.addEventListener('error', () => {
                clearTimeout(timeout);
                entry.state = 'error';
                reject(new Error('Video load error'));
            }, { once: true });

            // Trigger load
            video.load();
        });
    }

    /**
     * Transition from element to video
     */
    private transitionToVideo(entry: ActiveVideo): void {
        // Show video
        entry.videoElement.style.opacity = '1';

        // Hide element (but keep it for fallback)
        entry.targetElement.style.opacity = '0';

        // Remove any CSS animations from element
        entry.targetElement.style.animation = 'none';
    }

    /**
     * Transition from video back to element
     */
    private transitionToImage(entry: ActiveVideo): void {
        // Show element
        entry.targetElement.style.opacity = '1';

        // Hide video
        entry.videoElement.style.opacity = '0';

        // Pause video
        entry.videoElement.pause();
    }

    /**
     * Cleanup video element and restore element
     */
    private cleanup(imageId: string): void {
        const entry = this.activeVideos.get(imageId);
        if (!entry) return;

        // Restore element
        entry.targetElement.style.opacity = '1';
        entry.targetElement.style.animation = '';
        entry.targetElement.style.position = '';
        entry.targetElement.style.zIndex = '';
        entry.targetElement.style.transition = '';

        // Remove video
        entry.videoElement.remove();

        // Unwrap if we created the wrapper
        if (entry.wrapperElement.classList.contains('beautifi-video-wrapper')) {
            const parent = entry.wrapperElement.parentNode;
            if (parent) {
                parent.insertBefore(entry.targetElement, entry.wrapperElement);
                entry.wrapperElement.remove();
            }
        }

        this.activeVideos.delete(imageId);
        this.log(`🧹 Cleaned up: ${imageId}`);
    }

    /**
     * Debug logging
     */
    private log(message: string): void {
        if (this.config.debug) {
            console.log(`[VideoRenderer] ${message}`);
        }
    }
}
